/**
 * Search over the bank master (Lookups.financialInstitutions): banks, foreign banks, finance companies. Matches the
 * code, the registered name, the Arabic name and the aliases ("Liv", "NBAD", "Noor Bank"). Pure.
 */
import type { Schema } from '@dealdesk/api-schema';

import { normalizeCountryText as normalizeText } from './countries';

export type FinancialInstitution = Schema<'FinancialInstitution'>;

const termsOf = (i: Pick<FinancialInstitution, 'code' | 'name' | 'nameAr' | 'aliases'>) => [i.code, i.name, i.nameAr ?? '', ...i.aliases].filter(Boolean).map(normalizeText);

/**
 * Institutions whose code, name, Arabic name or an alias starts with the query (or any of their words does) — case-,
 * accent- and Arabic-diacritic-insensitive. Each institution once, best match first (C3): an exact term ("FAB",
 * "Emirates NBD") before a term prefix before a word prefix, list order otherwise. The API's `q` uses the same rule.
 */
export function searchInstitutions<T extends Pick<FinancialInstitution, 'code' | 'name' | 'nameAr' | 'aliases'>>(q: string, list: readonly T[]): T[] {
  const n = normalizeText(q);
  if (!n) return [...list];
  const word = (w: string) => w.startsWith(n) || (w.startsWith('ال') && w.slice(2).startsWith(n));
  const rank = (i: T) => {
    const terms = termsOf(i);
    if (terms.includes(n)) return 0;
    if (terms.some((t) => t.startsWith(n))) return 1;
    return terms.some((t) => t.split(' ').some(word)) ? 2 : -1;
  };
  return list
    .map((i, at) => ({ i, at, r: rank(i) }))
    .filter((x) => x.r >= 0)
    .sort((a, b) => a.r - b.r || a.at - b.at)
    .map((x) => x.i);
}

/** "ENBD · Emirates NBD" (registered name; never translated, except the institution's own Arabic name in Arabic). */
export function institutionLabel(i: Pick<FinancialInstitution, 'code' | 'name' | 'nameAr'>, locale: string): string {
  const name = locale.split('-')[0] === 'ar' && i.nameAr ? i.nameAr : i.name;
  return i.code === name ? name : `${i.code} · ${name}`;
}
